import "./style.scss";

import { Drawer } from "@mui/material";
import ActionBottom from "assets/img/svg/actionBottom";
import ActionTop from "assets/img/svg/actionTop";
import { useIsMobile } from "hooks/useIsMobile";
import type { ReactElement } from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition, SwitchTransition } from "react-transition-group";

interface ElementDetailsProps {
    open: boolean;
    onClose: () => void;
    elements: any[];
    elementIndex: number;
    setElementIndex: (index: number) => void;
    hideSwitchButtons?: boolean;
    children: ReactElement;
}

/**
 * Drawer component that adds buttons on the left to navigate to the next or previous element by clicking a button and some animations
 *
 * @param open              Boolean for the drawer component, decide wether it opens or not
 * @param onClose           Function called on drawer closing event
 * @param elements          Array of elements to show in details
 * @param elementIndex      Default index of the array, to find the right element to show
 * @param setElementIndex   Sets the new index to the parent component
 */

const ElementDetails = ({
    open,
    onClose,
    elements,
    elementIndex,
    setElementIndex,
    hideSwitchButtons = false,
    children,
}: ElementDetailsProps) => {
    const { isMobile } = useIsMobile();

    const [t] = useTranslation();
    const [transitionClass, setTransitionClass] = useState<"slide-up-transition" | "slide-down-transition">(
        "slide-up-transition",
    );
    const drawerContent = useRef<any>(null);

    const handlePreviousStatement = () => {
        const previousElement = elementIndex - 1 >= 0 ? elementIndex - 1 : elementIndex;
        setElementIndex(previousElement);
    };

    const handleNextStatement = () => {
        const nextElement = elementIndex + 1 < elements.length ? elementIndex + 1 : elementIndex;
        setElementIndex(nextElement);
    };

    return (
        <Drawer
            anchor={isMobile ? "bottom" : "right"}
            className="element-details"
            open={open}
            onClose={() => onClose()}
        >
            {!hideSwitchButtons && elements?.length > 0 && (
                <div className="element-details-switch-buttons">
                    <div
                        className={`element-details-top${elements[elementIndex - 1] == undefined ? " disabled" : ""}`}
                        onClick={handlePreviousStatement}
                        onMouseEnter={() => setTransitionClass("slide-up-transition")}
                    >
                        <div className="element-details-switch-button-title">
                            {elements[elementIndex - 1] == undefined ? (
                                ""
                            ) : (
                                <>
                                    <span>{t("ShowStatements.PreviousStatement")}</span>
                                    <span>{elements[elementIndex - 1]?.name}</span>
                                </>
                            )}
                        </div>
                        <ActionTop />
                    </div>
                    <div
                        className={`element-details-bottom${
                            elements[elementIndex + 1] == undefined ? " disabled" : ""
                        }`}
                        onClick={handleNextStatement}
                        onMouseEnter={() => setTransitionClass("slide-down-transition")}
                    >
                        <div className="element-details-switch-button-title">
                            {elements[elementIndex + 1] == undefined ? (
                                ""
                            ) : (
                                <>
                                    <span>{t("ShowStatements.NextStatement")}</span>
                                    <span>{elements[elementIndex + 1]?.name}</span>
                                </>
                            )}
                        </div>
                        <ActionBottom />
                    </div>
                </div>
            )}
            <SwitchTransition>
                <CSSTransition
                    key={elementIndex ?? 0}
                    nodeRef={drawerContent}
                    classNames={transitionClass}
                    addEndListener={(done: any) =>
                        drawerContent.current && drawerContent.current.addEventListener("transitionend", done, false)
                    }
                >
                    <div className="element-details-content" ref={drawerContent}>
                        {children}
                    </div>
                </CSSTransition>
            </SwitchTransition>
        </Drawer>
    );
};

export default ElementDetails;
